import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '@/api/client'
import { Footer } from '@/components/Footer'
import { ProviderButtons } from '@/components/ProviderButtons'
import { PublicHeader } from '@/components/PublicHeader'
import { Turnstile } from '@/components/Turnstile'

/**
 * What went wrong on the way back from a provider, in words.
 *
 * `already_linked` is the one that matters. It means the verified email came
 * back matching somebody who already has an account, so we handed this sign-in
 * a fresh empty person instead — correct, safe, and utterly baffling to
 * whoever it happens to. They signed in expecting their campaigns and got
 * nothing. Saying so beats an empty dashboard they have to work out.
 */
const NOTICES: Record<string, string> = {
  already_linked:
    'That email is already linked to another account. You are signed in to a ' +
    'new, empty one — sign out and use the account you first signed up with.',
  failed: 'That sign-in did not complete. Try again.',
  no_subject:
    'That provider did not tell us who you are. Try a different sign-in.',
}

export function SignIn() {
  const [params] = useSearchParams()
  const [address, setAddress] = useState('')
  const [code, setCode] = useState('')
  const [token, setToken] = useState('')
  const [sent, setSent] = useState(false)

  // The site key comes from the server with the disclosure, so a deploy
  // without Turnstile configured renders no widget rather than a broken one.
  const config = useQuery({
    queryKey: ['sms-disclosure'],
    queryFn: () => api.get<{ turnstile_site_key: string }>('/api/sms/disclosure'),
  })
  const siteKey = config.data?.turnstile_site_key ?? ''

  const request = useMutation({
    mutationFn: () =>
      api.post('/auth/code/request', { address, turnstile_token: token }),
    onSuccess: () => setSent(true),
  })

  const verify = useMutation({
    mutationFn: () => api.post('/auth/code/verify', { address, code }),
    // A full navigation, not a router push: the session is new and every
    // cached query on the old one has to go.
    onSuccess: () => window.location.assign(next),
  })

  const next = params.get('next') || '/'
  const notice = NOTICES[params.get('notice') || params.get('error') || '']

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: 380 }}>
        <PublicHeader size={30} />
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          You paid. Now get paid back.
        </Typography>

        {notice && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.8125rem' }}>
            {notice}
          </Alert>
        )}

        <ProviderButtons next={next} />

        {/* The other way in, and for somebody whose contact detail is a phone
            number it is the only one. One field: an "@" decides whether the
            code goes by email or by text, because asking somebody to pick
            first is a radio button in front of a sign-in box. */}
        <Divider sx={{ my: 2.5, fontSize: '0.75rem', color: 'text.secondary' }}>
          or
        </Divider>

        {sent ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              We sent a code to <strong>{address}</strong>. It works once and
              expires in ten minutes.
            </Typography>
            <TextField
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
            />
            {verify.isError && (
              <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
                {(verify.error as Error).message}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={code.trim().length < 6 || verify.isPending}
              onClick={() => verify.mutate()}
            >
              Sign in
            </Button>
            <Button size="small" onClick={() => { setSent(false); setCode('') }}>
              Use a different address
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              label="Email or mobile"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              fullWidth
              placeholder="you@example.com  or  555-013-4567"
            />
            {siteKey && <Turnstile siteKey={siteKey} onToken={setToken} />}
            {request.isError && (
              <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
                {(request.error as Error).message}
              </Alert>
            )}
            <Button
              variant="outlined"
              size="large"
              disabled={!address.trim() || request.isPending}
              onClick={() => request.mutate()}
            >
              Send me a code
            </Button>
          </Stack>
        )}

        <Typography variant="caption" sx={{ display: 'block', mt: 3 }}>
          We only ever ask a provider for your name and email, and we only
          adopt an existing account when the provider says the address is
          verified.
        </Typography>
        <Footer dense />
      </Paper>
    </Box>
  )
}
